import { useTranslation } from "react-i18next";
import { AutoPlaySettings } from "../../hooks/useAutoPlay";
import { Checkbox, Typography } from "../../shared/ui";
import { NumberInput } from "../../shared/ui/NumberInput";
import { Icon } from "../Icon";
import css from "./AutoPlayOptions.module.scss";

interface AutoPlayOptionsProps {
  onClose: () => void;
  onStart: (settings: AutoPlaySettings) => boolean | void;
  settings?: AutoPlaySettings;
  onChange: (patch: Partial<AutoPlaySettings>) => void;
  onReset: () => void;
}
const roundOptions = [10, 20, 50, 100];

export const AutoPlayOptions = ({
  onClose,
  onStart,
  settings,
  onChange,
  onReset,
}: AutoPlayOptionsProps) => {
  const { t } = useTranslation();
  const s: AutoPlaySettings = settings ?? {
    numberOfRounds: 50,
    stopOnWin: false,
    stopOnWinAmount: 1,
    stopOnLoss: true,
    stopOnLossAmount: 1,
    stopOnBalance: false,
    stopOnBalanceAmount: 1,
  };

  const handleReset = () => {
    onReset();
  };

  const handleStart = () => {
    const res = onStart(s);
    if (res !== false) {
      onClose();
    }
  };

  return (
    <div className={css.container}>
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <Icon name="close" width={20} height={20} className={css.closeIcon} />
      </button>
      <Typography variant="h2" color="header">
        {t("main.auto_play_options")}
      </Typography>
      <div className={css.roundsSection}>
        <Typography variant="caption2" color="header-small">
          {t("main.number_of_rounds")}
        </Typography>
        <div className={css.roundsContainer}>
          {roundOptions.map((option) => (
            <button
              key={option}
              className={`${css.roundOption} ${
                s.numberOfRounds === option ? css.active : ""
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange({ numberOfRounds: option });
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                onChange({ numberOfRounds: option });
              }}
            >
              <p>{option}</p>
            </button>
          ))}
        </div>
      </div>
      <div className={css.optionsContainer}>
        <div className={css.optionRow}>
          <Checkbox
            checked={s.stopOnLoss}
            onChange={(v) => onChange({ stopOnLoss: v })}
          />
          <Typography variant="caption2" color="header-small">
            {t("main.stop_if_cash_decreases_by")}
          </Typography>
          <div className={css.amountContainer}>
            <NumberInput
              value={s.stopOnLossAmount}
              min={1}
              step={1}
              onChange={(n) => onChange({ stopOnLossAmount: n })}
              disabled={!s.stopOnLoss}
            />
            <Typography variant="caption3" color="light-grey">
              USD
            </Typography>
          </div>
        </div>
        <div className={css.optionRow}>
          <Checkbox
            checked={s.stopOnWin}
            onChange={(v) => onChange({ stopOnWin: v })}
          />
          <Typography variant="caption2" color="header-small">
            {t("main.stop_if_cash_increases_by")}
          </Typography>
          <div className={css.amountContainer}>
            <NumberInput
              value={s.stopOnWinAmount}
              min={1}
              step={1}
              onChange={(n) => onChange({ stopOnWinAmount: n })}
              disabled={!s.stopOnWin}
            />
            <Typography variant="caption3" color="light-grey">
              USD
            </Typography>
          </div>
        </div>
        <div className={css.optionRow}>
          <Checkbox
            checked={s.stopOnBalance}
            onChange={(v) => onChange({ stopOnBalance: v })}
          />
          <Typography variant="caption2" color="header-small">
            {t("main.stop_if_single_win_exceeds")}
          </Typography>
          <div className={css.amountContainer}>
            <NumberInput
              value={s.stopOnBalanceAmount}
              min={1}
              step={1}
              onChange={(n) => onChange({ stopOnBalanceAmount: n })}
              disabled={!s.stopOnBalance}
            />
            <Typography variant="caption3" color="light-grey">
              USD
            </Typography>
          </div>
        </div>
      </div>
      <div className={css.buttonsContainer}>
        <button
          className={css.resetButton}
          onMouseDown={(e) => {
            e.preventDefault();
            handleReset();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleReset();
          }}
        >
          <Typography variant="button1" color="orange">
            {t("main.reset")}
          </Typography>
        </button>
        <button
          className={css.startButton}
          onMouseDown={(e) => {
            e.preventDefault();
            handleStart();
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            handleStart();
          }}
        >
          <Typography variant="button1" color="black">
            {t("main.start")}
          </Typography>
        </button>
      </div>
    </div>
  );
};
